import { useState } from 'react'
import {
  useActivityLogs,
  useActivityLogsStats,
  parseLogDetails,
  EVENT_TYPE_LABELS,
  CATEGORY_CONFIG,
  type ActivityLogsFilters,
} from '../hooks/useActivityLogs'
import { useSites } from '../../sites/hooks/useSites'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Activity,
  ChevronDown,
  ChevronUp,
  Globe,
  User,
  Puzzle,
  Palette,
  Settings,
  FileText,
  Shield,
} from 'lucide-react'
import { formatDistanceToNow, format } from 'date-fns'

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  auth: <Shield className="h-4 w-4" />,
  user: <User className="h-4 w-4" />,
  plugin: <Puzzle className="h-4 w-4" />,
  theme: <Palette className="h-4 w-4" />,
  option: <Settings className="h-4 w-4" />,
  post: <FileText className="h-4 w-4" />,
}

function CategoryBadge({ category }: { category: string }) {
  const config = CATEGORY_CONFIG[category] || { label: category, color: 'bg-gray-500' }
  return (
    <Badge variant="secondary" className={`${config.color} text-white flex items-center gap-1 w-fit`}>
      {CATEGORY_ICONS[category]}
      {config.label}
    </Badge>
  )
}

function StatsCards({ stats }: { stats: ReturnType<typeof useActivityLogsStats>['data'] }) {
  if (!stats) return null

  return (
    <div className="grid gap-4 md:grid-cols-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Total Events</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.total_events.toLocaleString()}</div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Last 24 Hours</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.events_last_24h.toLocaleString()}</div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Active Sites</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.by_site.length}</div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Event Types</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.top_events.length}</div>
        </CardContent>
      </Card>
    </div>
  )
}

function LogsTable({
  logs,
  isLoading,
  expandedId,
  onToggleExpand,
}: {
  logs: ReturnType<typeof useActivityLogs>['data']
  isLoading: boolean
  expandedId: string | null
  onToggleExpand: (id: string) => void
}) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    )
  }

  if (!logs || logs.logs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Activity className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium">No activity logs yet</h3>
        <p className="text-muted-foreground">
          Activity will appear here when WordPress sites send events
        </p>
      </div>
    )
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[40px]"></TableHead>
          <TableHead>Site</TableHead>
          <TableHead>Event</TableHead>
          <TableHead>Category</TableHead>
          <TableHead>User</TableHead>
          <TableHead>Time</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {logs.logs.map((log) => {
          const isExpanded = expandedId === log.id
          const details = parseLogDetails(log.details)

          return (
            <>
              <TableRow
                key={log.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => onToggleExpand(log.id)}
              >
                <TableCell>
                  <Button variant="ghost" size="icon" className="h-6 w-6">
                    {isExpanded ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </Button>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-muted-foreground" />
                    <code className="text-sm font-mono">{log.site_domain}</code>
                  </div>
                </TableCell>
                <TableCell>
                  <span className="font-medium">
                    {EVENT_TYPE_LABELS[log.event_type] || log.event_type}
                  </span>
                  {log.object_name && (
                    <span className="text-muted-foreground ml-1">({log.object_name})</span>
                  )}
                </TableCell>
                <TableCell>
                  <CategoryBadge category={log.category} />
                </TableCell>
                <TableCell>
                  {log.user_email ? (
                    <div className="flex flex-col">
                      <span className="text-sm">{log.user_name || log.user_email}</span>
                      {log.user_name && (
                        <span className="text-xs text-muted-foreground">{log.user_email}</span>
                      )}
                    </div>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  <span title={format(new Date(log.created_at), 'PPpp')}>
                    {formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}
                  </span>
                </TableCell>
              </TableRow>
              {isExpanded && (
                <TableRow key={`${log.id}-details`}>
                  <TableCell colSpan={6} className="bg-muted/30">
                    <div className="p-4 space-y-2">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Event Type:</span>{' '}
                          <code>{log.event_type}</code>
                        </div>
                        {log.object_type && (
                          <div>
                            <span className="text-muted-foreground">Object Type:</span>{' '}
                            <code>{log.object_type}</code>
                          </div>
                        )}
                        {log.object_name && (
                          <div>
                            <span className="text-muted-foreground">Object Name:</span>{' '}
                            <code>{log.object_name}</code>
                          </div>
                        )}
                        {log.ip_address && (
                          <div>
                            <span className="text-muted-foreground">IP Address:</span>{' '}
                            <code>{log.ip_address}</code>
                          </div>
                        )}
                        <div>
                          <span className="text-muted-foreground">Full Timestamp:</span>{' '}
                          {format(new Date(log.created_at), 'PPpp')}
                        </div>
                      </div>
                      {details && Object.keys(details).length > 0 && (
                        <div>
                          <span className="text-muted-foreground text-sm">Details:</span>
                          <pre className="mt-1 p-2 bg-muted rounded text-xs overflow-auto max-h-32">
                            {JSON.stringify(details, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </>
          )
        })}
      </TableBody>
    </Table>
  )
}

export function ActivityLogsContent() {
  const [filters, setFilters] = useState<ActivityLogsFilters>({
    limit: 50,
    offset: 0,
  })
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const { data: logs, isLoading } = useActivityLogs(filters)
  const { data: stats } = useActivityLogsStats()
  const { data: sites } = useSites()

  const handleFilterChange = (key: keyof ActivityLogsFilters, value: string | undefined) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value === 'all' ? undefined : value,
      offset: 0, // Reset to first page on filter change
    }))
  }

  const handleNextPage = () => {
    if (logs && logs.offset + logs.limit < logs.total) {
      setFilters((prev) => ({
        ...prev,
        offset: (prev.offset || 0) + (prev.limit || 50),
      }))
    }
  }

  const handlePrevPage = () => {
    if (filters.offset && filters.offset > 0) {
      setFilters((prev) => ({
        ...prev,
        offset: Math.max(0, (prev.offset || 0) - (prev.limit || 50)),
      }))
    }
  }

  const categories = Object.entries(CATEGORY_CONFIG).map(([value, config]) => ({
    value,
    label: config.label,
  }))

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Stats Cards */}
      <StatsCards stats={stats} />

      {/* Filters */}
      <Card>
        <CardHeader className="p-4 pb-3 md:p-6 md:pb-3">
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0 md:p-6 md:pt-0">
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:gap-4">
            {/* Site Filter */}
            <div className="w-full sm:w-[200px]">
              <Select
                value={filters.site || 'all'}
                onValueChange={(value) => handleFilterChange('site', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All sites" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sites</SelectItem>
                  {sites?.map((site) => (
                    <SelectItem key={site.domain} value={site.domain}>
                      {site.domain}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Category Filter */}
            <div className="w-full sm:w-[150px]">
              <Select
                value={filters.category || 'all'}
                onValueChange={(value) => handleFilterChange('category', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Event Type Filter */}
            <div className="w-full sm:w-[180px]">
              <Select
                value={filters.event_type || 'all'}
                onValueChange={(value) => handleFilterChange('event_type', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All events" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All events</SelectItem>
                  {Object.entries(EVENT_TYPE_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardHeader className="p-4 pb-2 md:p-6 md:pb-2">
          <CardTitle>Activity Logs</CardTitle>
          <CardDescription>
            {isLoading
              ? 'Loading...'
              : logs
                ? `Showing ${logs.offset + 1}-${Math.min(logs.offset + logs.limit, logs.total)} of ${logs.total.toLocaleString()} events`
                : 'No events'}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 pt-0 md:p-6 md:pt-0">
          <LogsTable
            logs={logs}
            isLoading={isLoading}
            expandedId={expandedId}
            onToggleExpand={(id) => setExpandedId(expandedId === id ? null : id)}
          />

          {/* Pagination */}
          {logs && logs.total > logs.limit && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <div className="text-sm text-muted-foreground">
                Page {Math.floor((logs.offset || 0) / logs.limit) + 1} of{' '}
                {Math.ceil(logs.total / logs.limit)}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrevPage}
                  disabled={!filters.offset || filters.offset === 0}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNextPage}
                  disabled={logs.offset + logs.limit >= logs.total}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
